// Data the compose drawer needs: usable templates in the user's locale and the template variables.
import { useGetIdentity, useGetList, useGetOne, useLocaleState } from "ra-core";
import { useMemo } from "react";
import {
  buildTemplateVars,
  resolveEmailConfig,
  type TemplateRow,
} from "../../domain/email";
import {
  COMPOSE_TEMPLATE_KINDS,
  isUsableTemplate,
  suggestedRecipients,
} from "./composeModel";

type Ids = {
  open: boolean;
  companyId: number | null;
  contactId: number | null;
  dealId: number | null;
};
export type ComposeTemplate = TemplateRow & {
  attachments?: unknown; // GAVION template attachments
  draft?: boolean | null;
  archived?: boolean | null;
  approved_at?: string | null;
};

export function useComposeData({ open, companyId, contactId, dealId }: Ids) {
  const [locale = "fr"] = useLocaleState();
  const { data: rows = [] } = useGetList<ComposeTemplate>(
    "email_templates",
    {
      // Approved templates only (spec 08 §3): drafts and archived ones are never offered.
      filter: {
        // Kinds are narrowed client-side: the FakeRest `@in` parser rejects underscores.
        "kind@neq": "marketing",
        language: locale === "en" ? "en" : "fr",
        draft: false,
        archived: false,
      },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: open },
  );
  const templates = useMemo(
    () =>
      rows.filter(
        (t) =>
          (COMPOSE_TEMPLATE_KINDS as readonly string[]).includes(t.kind) &&
          isUsableTemplate(t),
      ),
    [rows],
  );
  const { data: company } = useGetOne(
    "companies",
    { id: companyId ?? 0 },
    { enabled: open && companyId != null },
  );
  const { data: contact } = useGetOne(
    "contacts",
    { id: contactId ?? 0 },
    { enabled: open && contactId != null },
  );
  const { data: deal } = useGetOne(
    "deals",
    { id: dealId ?? 0 },
    { enabled: open && dealId != null },
  );
  const { identity } = useGetIdentity();
  const repId = identity?.id;
  const { data: rep } = useGetOne(
    "sales",
    { id: repId ?? 0 },
    { enabled: open && repId != null && repId !== 0 },
  );
  const { data: cfgRows = [] } = useGetList(
    "configuration",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: open },
  );
  const vars = useMemo(
    () =>
      buildTemplateVars(
        {
          company: company ?? null,
          contact: contact ?? null,
          deal: deal ?? null,
          rep: rep ?? null,
        },
        resolveEmailConfig(cfgRows[0]?.config),
      ),
    [company, contact, deal, rep, cfgRows],
  );
  const suggestedTo = useMemo(
    () => suggestedRecipients(contact ?? null, company ?? null),
    [contact, company],
  );
  return { templates, vars, suggestedTo };
}
